import type { Plan } from '@/domain/types';
import { formatTimeDisplay } from '@/domain/dates';

function hourPart(hm: string | null): string | null {
  if (!hm) return null;
  const [h] = hm.split(':');
  return h?.padStart(2, '0') ?? null;
}

function hasNonZeroMinutes(hm: string | null): boolean {
  if (!hm) return false;
  const parts = hm.split(':');
  return Boolean(parts[1] && parts[1] !== '00');
}

/** Full label: `12:00–14:30` or all-day text. Never invents times. */
export function formatPlanTimeLabel(
  plan: Pick<Plan, 'isAllDay' | 'startTime' | 'endTime'>,
  timeFormat: '12h' | '24h',
  allDayLabel: string,
): string {
  if (plan.isAllDay) return allDayLabel;
  const start = formatTimeDisplay(plan.startTime, timeFormat);
  const end = formatTimeDisplay(plan.endTime, timeFormat);
  if (start && end) return `${start}–${end}`;
  if (start) return start;
  return '';
}

/**
 * Compact range for narrow cells: whole hours → `05–14`;
 * with minutes → keep `05:30–14:15`. All-day stays full label.
 */
export function formatPlanTimeLabelCompact(
  plan: Pick<Plan, 'isAllDay' | 'startTime' | 'endTime'>,
  timeFormat: '12h' | '24h',
  allDayLabel: string,
): string {
  if (plan.isAllDay) return allDayLabel;
  if (timeFormat === '12h') {
    return formatPlanTimeLabel(plan, timeFormat, allDayLabel);
  }
  const start = plan.startTime;
  const end = plan.endTime;
  if (!start) return '';
  const needsMinutes = hasNonZeroMinutes(start) || hasNonZeroMinutes(end);
  if (needsMinutes) {
    return formatPlanTimeLabel(plan, '24h', allDayLabel);
  }
  const sh = hourPart(start);
  const eh = hourPart(end);
  if (sh && eh) return `${sh}–${eh}`;
  if (sh) return sh;
  return '';
}

/**
 * Always returns profiles.display_name for the plan owner.
 * Never substitutes localized "You"/"Вы" for the current user.
 * Returns null while the profile is not yet hydrated (UI shows a skeleton).
 */
export function resolvePlanAuthorLabel(
  plan: Pick<Plan, 'ownerId'>,
  profiles: { id: string; displayName: string }[],
): string | null {
  const owner = profiles.find((p) => p.id === plan.ownerId);
  const name = owner?.displayName?.trim();
  return name || null;
}

export function formatExtraMediaCount(count: number): string {
  if (count <= 1) return '';
  return `+${count - 1}`;
}
